import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { useNursingHome } from "@/hooks/use-nursing-home";
import { ClaimDialog } from "@/components/nursing-homes/ClaimDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";

export default function ClaimNursingHomePage() {
  const { nursingHomeSlug } = useParams<{ nursingHomeSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [claimDialogOpen, setClaimDialogOpen] = useState(true);
  const [checkingClaim, setCheckingClaim] = useState(false);
  const [formData, setFormData] = useState({
    role: "",
    email: "",
    contactPhone: "",
    verificationNote: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: nursingHome, isLoading, error } = useNursingHome(nursingHomeSlug);
  
  useEffect(() => {
    // Auto-open claim dialog when page loads
    if (nursingHome && !nursingHome.claimed) {
      setClaimDialogOpen(true);
    }
  }, [nursingHome]);
  
  // If the nursing home is already claimed, show a different message
  if (nursingHome?.claimed) {
    return (
      <Layout>
        <SEOHead 
          title={`${nursingHome.center_name} - Already Claimed | Grandpal`}
          description="This nursing home profile has already been claimed."
        />
        <div className="container py-16 max-w-3xl">
          <Card>
            <CardHeader>
              <CardTitle className="text-center">Profile Already Claimed</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center">
                The profile for <strong>{nursingHome.center_name}</strong> has already been claimed.
              </p>
              <p className="text-center text-muted-foreground">
                If you believe this is an error or you should have access to this profile, 
                please contact our support team.
              </p>
              <div className="flex justify-center pt-4">
                <Button asChild>
                  <Link to={`/nursing-homes/${nursingHome.county_slug}/${nursingHome.nh_slug}`}>
                    View Nursing Home Profile
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const onDialogOpenChange = (open: boolean) => {
    setClaimDialogOpen(open);
    // If the dialog is closed, navigate to the nursing home page
    if (!open && nursingHome) {
      const nursingHomeUrl = `/nursing-homes/${nursingHome.county_slug}/${nursingHome.nh_slug}`;
      navigate(nursingHomeUrl);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value: string) => {
    setFormData({ ...formData, role: value });
  };

  const handleClaimSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      if (!user) {
        // Set flag to identify this user as a nursing home owner after authentication
        sessionStorage.setItem('justClaimedNursingHome', nursingHomeSlug || '');
        return;
      }
      
      const { error } = await supabase.from("nursing_home_claims").insert({
        nursing_home_id: nursingHomeSlug,
        user_id: user.id,
        status: 'pending_review',
        verification_documents: formData,
        notes: formData.verificationNote,
      });

      if (error) throw error;
      
      toast.success("Your claim request has been submitted");
      
      // Set flag for redirection after auth
      sessionStorage.setItem('justClaimedNursingHome', nursingHomeSlug || '');
      
      // Redirect to owner dashboard
      navigate('/owner/dashboard');
      
    } catch (error) {
      console.error("Error submitting claim:", error);
      toast.error("Failed to submit claim");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEOHead 
        title={isLoading ? "Claim Nursing Home | Grandpal" : `Claim ${nursingHome?.center_name} | Grandpal`}
        description="Claim your nursing home profile on Grandpal to manage your information and connect with families."
      />

      <div className="container py-8 max-w-3xl">
        {isLoading || checkingClaim ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center justify-center text-center">
                <h2 className="text-2xl font-bold mb-2">Nursing Home Not Found</h2>
                <p className="text-muted-foreground mb-4">
                  We couldn't find the nursing home you're looking for. Please check the link and try again.
                </p>
                <Button asChild>
                  <Link to="/nursing-homes">Browse Nursing Homes</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : nursingHome ? (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Claim Your Nursing Home Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4">
                  You're about to claim the profile for <strong>{nursingHome.center_name}</strong>. 
                  Claiming this profile will allow you to:
                </p>
                <ul className="list-disc pl-5 mb-4 space-y-1">
                  <li>Update your nursing home's information and photos</li>
                  <li>Respond to inquiries from families</li>
                  <li>Access qualified leads who are interested in your facility</li>
                  <li>Highlight special features and amenities</li>
                </ul>
                <Button 
                  onClick={() => setClaimDialogOpen(true)}
                  className="w-full mt-4 bg-grandpal-green-dark hover:bg-grandpal-green-darkHover"
                >
                  Claim This Profile
                </Button>
              </CardContent>
            </Card>

            <ClaimDialog
              centerName={nursingHome.center_name}
              open={claimDialogOpen}
              onOpenChange={onDialogOpenChange}
              isSubmitting={isSubmitting}
              formData={formData}
              onInputChange={handleInputChange}
              onSelectChange={handleSelectChange}
              onSubmit={handleClaimSubmit}
            />
          </>
        ) : null}
      </div>
    </Layout>
  );
}
